/**
 * pageCatalog — static map of the 6 analytics pages the advisor understands.
 *
 * Consumed by: C2 (query_kpi), C3 (describe_page), C5 (propose_navigation),
 * D4 (serializer), D5 (assembler).
 *
 * Source of truth for routes: frontend/src/routes/router.tsx (nested Routes)
 * + frontend/src/constants/routes.ts (parent segments).
 * The live fact table is per-(page,kpi) in the chart_registry (see PageInfo.factTable
 * below — this catalog only carries the static fallback the page components pass in).
 * Source of truth for default KPIs: frontend/src/components/ReviewPage/pageDefaults.ts.
 */

export interface PageInfo {
  id: string
  route: string
  /**
   * Static fallback fact table — mirrors each page component's static factTable prop
   * (the value passed to useReviewPageState). The live fact is per-(page,kpi) and comes
   * from the chart_registry: query_kpi resolves it via get_charts and does NOT use this
   * field, while describe_page uses it only as a fallback when the registry carries no
   * string fact (the same fallback useReviewPageState applies for ratio-default pages).
   */
  factTable: string
  defaultKpi: string
}

/**
 * Keyed by pageId (matches PAGE_DEFAULTS keys in pageDefaults.ts).
 * Routes are canonical full paths (NOT legacy redirect paths).
 *
 * Uses `satisfies Record<string, PageInfo>` (not an explicit type annotation) so
 * that TypeScript retains the concrete string-literal key types. This lets tests
 * and static callers access known keys without `| undefined`, while still
 * conforming to the `Record<string, PageInfo>` interface expected by dynamic
 * callers (C2, C3, C5, D4, D5) that receive a pageId at runtime.
 */
export const PAGE_CATALOG = {
  ReviewKpis: {
    id: 'ReviewKpis',
    route: '/review-status/review-kpis',
    factTable: 'media_national',
    defaultKpi: 'actual_sales',
  },
  MarketingInvests: {
    id: 'MarketingInvests',
    route: '/review-status/marketing-invests',
    factTable: 'media_national',
    defaultKpi: 'nsp',
  },
  CampaignPerformance: {
    id: 'CampaignPerformance',
    route: '/review-performance/campaign-performance',
    factTable: 'model_tracking',
    defaultKpi: 'sales_roi__nsp__npv',
  },
  Contribution: {
    id: 'Contribution',
    route: '/review-performance/contribution',
    factTable: 'model_success_kpi',
    defaultKpi: 'sales__media__units',
  },
  Dynamics: {
    id: 'Dynamics',
    route: '/review-performance/dynamics',
    factTable: 'model_tracking',
    defaultKpi: 'sales_roi__nsp__npv',
  },
  MultiDeepDive: {
    id: 'MultiDeepDive',
    route: '/review-performance/multi-deep-dive',
    factTable: 'model_tracking',
    defaultKpi: 'sales_roi__nsp__npv',
  },
} satisfies Record<string, PageInfo>

/**
 * Return the pageId whose canonical route matches `pathname`.
 *
 * Strategy:
 * 1. Exact match first.
 * 2. Else prefix match — the entry whose `route` is a prefix of `pathname`
 *    AND the next character in `pathname` is `/` (prevents `/review-status/review-kpisextra`
 *    from matching `/review-status/review-kpis`). Pick the LONGEST such prefix so no
 *    shallower sibling shadows a deeper one.
 * 3. Return `null` if no match.
 */
export function pageIdForPath(pathname: string): string | null {
  // 1. Exact match
  for (const [id, info] of Object.entries(PAGE_CATALOG)) {
    if (info.route === pathname) return id
  }

  // 2. Longest-prefix match
  let bestId: string | null = null
  let bestLen = -1

  for (const [id, info] of Object.entries(PAGE_CATALOG)) {
    const { route } = info
    if (
      pathname.startsWith(route) &&
      (pathname.length === route.length || pathname[route.length] === '/')
    ) {
      if (route.length > bestLen) {
        bestLen = route.length
        bestId = id
      }
    }
  }

  return bestId
}
